// use-insight-rail-data.ts
//
// The reads behind the competitive insight rail on Brand Monitoring.
//
// Three inputs, all read-only, all from endpoints this product already calls:
//
//   * rates + movement   the ranking feed this section already loaded. Using it
//                        rather than the Deep Signal competitive payload keeps
//                        the rail's numbers consistent with the table it sits
//                        directly under: same instrument, same read.
//   * per-model rates    only Deep Signal's payload carries per-model rates for
//                        competitors AND the measured brand, so the detector
//                        that locates a rival's advantage needs that one extra
//                        read. Same endpoints the Deep Signal tab uses.
//   * attribute scores   fetchAttributes, scoped to the customer + run the
//                        ranking feed already returned.
//
// Each read degrades on its own: if the Deep Signal chain fails, the detectors
// that need per-model data return null and the rest of the pool still deals.
import { useEffect, useMemo, useState } from "react";
import {
  fetchAttributes,
  type AttributeResult, type MeasuredCompetitiveV2,
} from "@/app/services/brand/competitive-intelligence-api";
import { listDeepSignalCustomers, listDeepSignalRuns, getDeepSignalData } from "@/api/deep-signal-api";
import { buildCompetitiveFromApi } from "@/app/components/deep-signal/builders";
import type { MeasuredModel } from "@/app/components/deep-signal/types";
import { buildFindingPool, dealViews, type RailFinding } from "./competitive-detectors";

interface PerModel {
  byBrand: Record<string, Record<string, number | null> | undefined>;
  focal: Record<string, number | null>;
  models: MeasuredModel[];
}

const EMPTY_PER_MODEL: PerModel = { byBrand: {}, focal: {}, models: [] };

export function useInsightRailData(
  brandName: string | undefined,
  feed: MeasuredCompetitiveV2 | null,
): RailFinding[][] {
  const [attrs, setAttrs] = useState<AttributeResult[]>([]);
  const [perModel, setPerModel] = useState<PerModel>(EMPTY_PER_MODEL);
  const [priorReadMonth, setPriorReadMonth] = useState<string | null>(null);

  const customerId = feed?.customerId ?? null;
  const runId = feed?.run?.id ?? null;
  const previousRunId = feed?.ranking?.previous_run_id ?? null;

  // Attribute scores: the attribute-shaped detectors.
  useEffect(() => {
    if (customerId == null || runId == null) return;
    let alive = true;
    fetchAttributes(customerId, runId)
      .then((a) => { if (alive) setAttrs(a.attributes || []); })
      .catch(() => { if (alive) setAttrs([]); });
    return () => { alive = false; };
  }, [customerId, runId]);

  // Per-model rates, off the latest run, which is what a glanceable read of
  // "where is this happening" should reflect.
  useEffect(() => {
    const brand = (brandName || "").trim();
    if (!brand) return;
    let alive = true;
    listDeepSignalCustomers()
      .then((res) => {
        if (!alive) return undefined;
        const q = brand.toLowerCase();
        const match = res.customers.find(
          (c) => c.brand_name.toLowerCase().includes(q) || q.includes(c.brand_name.toLowerCase()),
        );
        if (!match?.latest_run_id) return undefined;
        return getDeepSignalData(match.id, match.latest_run_id);
      })
      .then((data) => {
        if (!alive || !data) return;
        const built = buildCompetitiveFromApi(data);
        const byBrand: PerModel["byBrand"] = {};
        for (const row of built.funnelRows) byBrand[row.brand] = row.perModel;
        // The measured brand's own per-model rates live on the focal row, which
        // the funnel builder does not carry (the table shows competitors only).
        const focalPer = data.competitive?.focal_brand?.per_model ?? {};
        const focal: Record<string, number | null> = {};
        for (const m of built.models) {
          const v = focalPer[m.key]?.mention_rate;
          focal[m.key] = v == null ? null : v * 100;
        }
        setPerModel({ byBrand, focal, models: built.models });
      })
      .catch(() => { if (alive) setPerModel(EMPTY_PER_MODEL); });
    return () => { alive = false; };
  }, [brandName]);

  // The previous read's month, for the climbing detector. Only fetched when
  // there is actually movement to date: the feed leaves deltas null until a
  // same-instrument prior read exists, so without one there is nothing to label.
  const hasMovement = useMemo(
    () => (feed?.ranking?.competitors ?? []).some((c) => c.deltas?.primary_rec != null),
    [feed],
  );

  useEffect(() => {
    if (!hasMovement || customerId == null || previousRunId == null) {
      setPriorReadMonth(null);
      return;
    }
    let alive = true;
    listDeepSignalRuns(customerId)
      .then((r) => {
        if (!alive) return;
        const prior = r.runs.find((run) => run.id === previousRunId);
        const raw = prior?.created_at;
        if (!raw) { setPriorReadMonth(null); return; }
        const d = new Date(raw);
        setPriorReadMonth(Number.isNaN(d.getTime())
          ? null
          : d.toLocaleDateString("en-US", { month: "long" }));
      })
      .catch(() => { if (alive) setPriorReadMonth(null); });
    return () => { alive = false; };
  }, [hasMovement, customerId, previousRunId]);

  // One pool per load, memoized: the order must not change between renders.
  return useMemo(() => {
    const competitors = feed?.ranking?.competitors ?? [];
    const focalName = feed?.focalName || brandName || "";
    if (!competitors.length && !attrs.length) return [];

    const pool = buildFindingPool({
      competitors,
      client: feed?.ranking?.client,
      focalName,
      attrs,
      models: perModel.models,
      perModelByBrand: perModel.byBrand,
      focalPerModel: perModel.focal,
      priorReadMonth,
    });
    return dealViews(pool, 5);
  }, [feed, brandName, attrs, perModel, priorReadMonth]);
}
